import {
  collectAnswerMessageIds,
  splitTurnSegments,
  type TurnSegment as SharedTurnSegment,
} from "@t3tools/shared/messageTimeline";
export { collectAnswerMessageIds, splitTurnSegments } from "@t3tools/shared/messageTimeline";
import * as Equal from "effect/Equal";
import { type TimelineEntry, type WorkLogEntry } from "../../session-logic";
import { type ChatMessage } from "../../types";
import { type TurnId, type TurnFailureReason } from "@t3tools/contracts";

type TurnSegment = SharedTurnSegment<TimelineEntry>;

export type TurnGroupItem =
  | {
      kind: "section";
      id: string;
      message: ChatMessage | null;
      label: string | null;
      entries: WorkLogEntry[];
      /** Steps still land here: the trailing run stays unfolded until one that breaks it arrives. */
      open: boolean;
    }
  | { kind: "message"; id: string; message: ChatMessage }
  | { kind: "question"; id: string; message: ChatMessage };

export type WorkLogGroup = {
  kind: "group";
  id: string;
  label: string;
  entries: WorkLogEntry[];
};

export type SectionRow = { kind: "entry"; entry: WorkLogEntry } | WorkLogGroup;

function joinsRun(entry: WorkLogEntry): boolean {
  return (
    entry.labelKey !== undefined &&
    entry.tone !== "error" &&
    entry.taskId === undefined &&
    entry.compactionPhase === undefined
  );
}

function sameRun(head: WorkLogEntry, next: WorkLogEntry): boolean {
  return (
    joinsRun(head) && joinsRun(next) && head.labelKey === next.labelKey && head.label === next.label
  );
}

export function groupWorkLogEntries(
  entries: ReadonlyArray<WorkLogEntry>,
  open = false,
): SectionRow[] {
  const runs: WorkLogEntry[][] = [];
  for (const entry of entries) {
    const run = runs.at(-1);
    const head = run?.[0];
    if (run !== undefined && head !== undefined && sameRun(head, entry)) {
      run.push(entry);
      continue;
    }
    runs.push([entry]);
  }
  return runs.flatMap((run, index): SectionRow[] => {
    const head = run[0];
    if (head === undefined) return [];
    const trailing = open && index === runs.length - 1;
    if (run.length < 2 || trailing) {
      return run.map((entry) => ({ kind: "entry", entry }));
    }
    return [{ kind: "group", id: head.id, label: head.label, entries: run }];
  });
}

export type TurnStatus =
  | { kind: "composing" }
  | { kind: "acting"; entries: WorkLogEntry[] }
  | { kind: "waiting_helpers"; entries: WorkLogEntry[] }
  | { kind: "thinking"; since: string | null };

export type MessagesTimelineRow =
  | { kind: "turn-failed"; id: string; createdAt: string; reason: TurnFailureReason }
  | {
      kind: "message";
      id: string;
      createdAt: string;
      message: ChatMessage;
      showAssistantCopyButton: boolean;
      assistantCopyStreaming: boolean;
    }
  | {
      kind: "turn-group";
      id: string;
      createdAt: string;
      startedAt: string;
      endedAt: string | null;
      live: boolean;
      items: TurnGroupItem[];
      sectionCount: number;
      status: TurnStatus | null;
    }
  | { kind: "working"; id: string; createdAt: string | null; label: string | null };

export interface StableMessagesTimelineRowsState {
  byId: Map<string, MessagesTimelineRow>;
  result: MessagesTimelineRow[];
}

export function normalizeCompactToolLabel(value: string): string {
  return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}

export function resolveAssistantMessageCopyState({
  text,
  showCopyButton,
  streaming,
}: {
  text: string | null;
  showCopyButton: boolean;
  streaming: boolean;
}) {
  const hasText = text !== null && text.trim().length > 0;
  return {
    text: hasText ? text : null,
    visible: showCopyButton && hasText && !streaming,
  };
}

export type MessageTimelineEntry = Extract<TimelineEntry, { kind: "message" }>;
type WorkTimelineEntry = Extract<TimelineEntry, { kind: "work" }>;
type SectionItem = Extract<TurnGroupItem, { kind: "section" }>;
type QuestionItem = Extract<TurnGroupItem, { kind: "question" }>;

/**
 * Steps still running belong to the live tail of the turn even when a message
 * the user sent mid-turn sits after them, so they render in the live status row.
 */
function hoistActiveWork(
  timelineEntries: ReadonlyArray<TimelineEntry>,
  activeTurnId: TurnId | null | undefined,
): ReadonlyArray<TimelineEntry> {
  if (activeTurnId == null) return timelineEntries;
  const active = timelineEntries.filter(
    (entry) =>
      isWorkEntry(entry) && entry.entry.active === true && entry.entry.turnId === activeTurnId,
  );
  if (active.length === 0) return timelineEntries;
  const activeIds = new Set(active.map((entry) => entry.id));
  return [...timelineEntries.filter((entry) => !activeIds.has(entry.id)), ...active];
}

function isWorkEntry(entry: TimelineEntry): entry is WorkTimelineEntry {
  return entry.kind === "work";
}

function isAssistantEntry(entry: TimelineEntry): entry is MessageTimelineEntry {
  return entry.kind === "message" && entry.message.role === "assistant";
}

function parseIsoMs(value: string | undefined | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function entryEndedAt(entry: TimelineEntry): string | null {
  if (isAssistantEntry(entry)) {
    return entry.message.completedAt ?? entry.message.createdAt;
  }
  if (entry.kind === "turn-failed") return entry.createdAt;
  if (entry.kind === "work") {
    return entry.entry.createdAt;
  }
  return null;
}

function latestIso(candidates: Iterable<string | null | undefined>): string | null {
  let latest: string | null = null;
  let latestMs: number | null = null;
  for (const candidate of candidates) {
    const ms = parseIsoMs(candidate);
    if (ms === null) continue;
    if (latestMs === null || ms > latestMs) {
      latestMs = ms;
      latest = candidate ?? null;
    }
  }
  return latest;
}

/**
 * Turn timing is derived from the items themselves rather than from turn
 * records: every message and activity carries a server-assigned timestamp, so
 * this works for every historical turn and always matches the collapsed content.
 */
function deriveTurnGroupTiming(segment: TurnSegment): {
  startedAt: string;
  endedAt: string | null;
} {
  const firstEntry = segment.entries[0];
  const startedAt = segment.userEntry?.createdAt ?? firstEntry?.createdAt ?? "";
  const startedMs = parseIsoMs(startedAt);
  const endedAt = latestIso(segment.entries.map(entryEndedAt));
  const endedMs = parseIsoMs(endedAt);
  if (startedMs === null || endedMs === null || endedMs <= startedMs) {
    return { startedAt, endedAt: null };
  }
  return { startedAt, endedAt };
}

function syntheticSectionLabel(entries: ReadonlyArray<WorkLogEntry>): string | null {
  const userEntry = entries.find((entry) => entry.audience !== "developer");
  return (userEntry ?? entries[0])?.label ?? null;
}

function buildTurnItems(
  entries: ReadonlyArray<TimelineEntry>,
  answerIds: ReadonlySet<string>,
  live: boolean,
): {
  items: TurnGroupItem[];
  firstGroupedEntry: TimelineEntry | null;
  activeEntries: WorkLogEntry[];
} {
  const slots: Array<SectionItem | QuestionItem> = [];
  const activeEntries: WorkLogEntry[] = [];
  let current: SectionItem | null = null;
  let tail: SectionItem | null = null;
  let firstGroupedEntry: TimelineEntry | null = null;
  for (const entry of entries) {
    if (isAssistantEntry(entry)) tail = null;
    if (isAssistantEntry(entry) && entry.message.phase === "question") {
      firstGroupedEntry ??= entry;
      current = null;
      slots.push({ kind: "question", id: entry.id, message: entry.message });
      continue;
    }
    if (isAssistantEntry(entry) && !answerIds.has(entry.message.id)) {
      firstGroupedEntry ??= entry;
      current = {
        kind: "section",
        id: entry.id,
        message: entry.message,
        label: null,
        entries: [],
        open: false,
      };
      slots.push(current);
      continue;
    }
    if (!isWorkEntry(entry)) continue;
    firstGroupedEntry ??= entry;
    if (entry.entry.active) {
      activeEntries.push(entry.entry);
      continue;
    }
    if (current === null) {
      current = {
        kind: "section",
        id: entry.id,
        message: null,
        label: null,
        entries: [],
        open: false,
      };
      slots.push(current);
    }
    current.entries.push(entry.entry);
    tail = current;
  }
  const last = tail?.entries.at(-1);
  if (live && tail !== null && last !== undefined) {
    tail.open = activeEntries.every((entry) => sameRun(last, entry));
  }
  const items: TurnGroupItem[] = [];
  for (const section of slots) {
    if (section.kind === "question") {
      items.push(section);
      continue;
    }
    if (section.entries.length > 0) {
      const entries = section.entries;
      items.push(
        section.message === null
          ? { ...section, entries, label: syntheticSectionLabel(entries) }
          : { ...section, entries },
      );
      continue;
    }
    if (section.message !== null) {
      items.push({ kind: "message", id: section.id, message: section.message });
    }
  }
  return { items, firstGroupedEntry, activeEntries };
}

function deriveTurnStatus(
  segment: TurnSegment,
  activeEntries: ReadonlyArray<WorkLogEntry>,
  startedAt: string | null,
): TurnStatus {
  if (segment.entries.some((entry) => isAssistantEntry(entry) && entry.message.streaming)) {
    return { kind: "composing" };
  }
  if (activeEntries.length > 0) {
    const entries = [...activeEntries];
    return activeEntries.some((entry) => entry.heartbeat === "waiting_helpers")
      ? { kind: "waiting_helpers", entries }
      : { kind: "acting", entries };
  }
  const since =
    latestIso([
      ...segment.entries.map(entryEndedAt),
      ...activeEntries.map((entry) => entry.startedAt ?? entry.createdAt),
      segment.userEntry?.createdAt,
    ]) ?? startedAt;
  return { kind: "thinking", since };
}

export function deriveMessagesTimelineRows(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  isWorking: boolean;
  workingLabel?: string | null;
  activeTurnInProgress?: boolean;
  activeTurnId?: TurnId | null;
  activeTurnStartedAt: string | null;
}): MessagesTimelineRow[] {
  const nextRows: MessagesTimelineRow[] = [];
  const segments = splitTurnSegments(
    input.activeTurnInProgress === true
      ? hoistActiveWork(input.timelineEntries, input.activeTurnId)
      : input.timelineEntries,
  );
  let liveGroupEmitted = false;

  const toMessageRow = (
    timelineEntry: MessageTimelineEntry,
    isAnswer: boolean,
  ): MessagesTimelineRow => {
    const { message } = timelineEntry;
    const assistantTurnStillInProgress =
      message.role === "assistant" &&
      input.activeTurnInProgress === true &&
      input.activeTurnId != null &&
      message.turnId === input.activeTurnId;
    return {
      kind: "message",
      id: timelineEntry.id,
      createdAt: timelineEntry.createdAt,
      message,
      showAssistantCopyButton: isAnswer,
      assistantCopyStreaming: message.streaming || assistantTurnStillInProgress,
    };
  };

  const pushTurnRows = (segment: TurnSegment, live: boolean) => {
    const { entries } = segment;
    const answerIds = collectAnswerMessageIds(entries, live);
    const { items, firstGroupedEntry, activeEntries } = buildTurnItems(entries, answerIds, live);
    for (const timelineEntry of entries) {
      if (timelineEntry === firstGroupedEntry) {
        const timing = deriveTurnGroupTiming(segment);
        const startedAt = live
          ? (latestIso([input.activeTurnStartedAt, segment.userEntry?.createdAt]) ??
            timing.startedAt)
          : timing.startedAt;
        nextRows.push({
          kind: "turn-group",
          id: `turn-group:${segment.userEntry?.id ?? timelineEntry.id}`,
          createdAt: timelineEntry.createdAt,
          startedAt,
          endedAt: live ? null : timing.endedAt,
          live,
          items,
          sectionCount: items.filter((item) => item.kind === "section").length,
          status: live ? deriveTurnStatus(segment, activeEntries, startedAt) : null,
        });
        liveGroupEmitted ||= live;
        continue;
      }
      if (
        timelineEntry.kind === "turn-failed" ||
        isWorkEntry(timelineEntry) ||
        (isAssistantEntry(timelineEntry) && !answerIds.has(timelineEntry.message.id))
      ) {
        continue;
      }
      nextRows.push(toMessageRow(timelineEntry, answerIds.has(timelineEntry.message.id)));
    }
    for (const entry of entries) {
      if (entry.kind === "turn-failed")
        nextRows.push({
          kind: "turn-failed",
          id: entry.id,
          createdAt: entry.createdAt,
          reason: entry.reason,
        });
    }
  };

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (!segment) continue;
    if (segment.userEntry) {
      nextRows.push(toMessageRow(segment.userEntry, false));
    }
    const isActiveSegment =
      input.activeTurnInProgress === true &&
      index === segments.length - 1 &&
      !segment.entries.some((entry) => entry.kind === "turn-failed");
    pushTurnRows(segment, isActiveSegment);
  }

  if (input.isWorking && !liveGroupEmitted) {
    nextRows.push({
      kind: "working",
      id: "working-indicator-row",
      createdAt: input.activeTurnStartedAt,
      label: input.workingLabel ?? null,
    });
  }

  return nextRows;
}

export function computeStableMessagesTimelineRows(
  rows: MessagesTimelineRow[],
  previous: StableMessagesTimelineRowsState,
): StableMessagesTimelineRowsState {
  const next = new Map<string, MessagesTimelineRow>();
  let anyChanged = rows.length !== previous.byId.size;

  const result = rows.map((row, index) => {
    const prevRow = previous.byId.get(row.id);
    const nextRow = prevRow && isRowUnchanged(prevRow, row) ? prevRow : row;
    next.set(row.id, nextRow);
    if (!anyChanged && previous.result[index] !== nextRow) {
      anyChanged = true;
    }
    return nextRow;
  });

  return anyChanged ? { byId: next, result } : previous;
}

function isTurnGroupItemUnchanged(a: TurnGroupItem, b: TurnGroupItem): boolean {
  if (a.kind !== b.kind || a.id !== b.id) return false;
  if (a.kind === "message" || a.kind === "question") {
    return a.message === (b as typeof a).message;
  }
  const bs = b as typeof a;
  return (
    a.message === bs.message &&
    a.label === bs.label &&
    a.open === bs.open &&
    Equal.equals(a.entries, bs.entries)
  );
}

function isTurnStatusUnchanged(a: TurnStatus | null, b: TurnStatus | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "composing":
      return true;
    case "thinking":
      return a.since === (b as typeof a).since;
    case "acting":
    case "waiting_helpers":
      return Equal.equals(a.entries, (b as typeof a).entries);
  }
}

/** Shallow field comparison per row variant — avoids deep equality cost. */
function isRowUnchanged(a: MessagesTimelineRow, b: MessagesTimelineRow): boolean {
  if (a.kind !== b.kind || a.id !== b.id) return false;

  switch (a.kind) {
    case "turn-failed":
      return a.createdAt === (b as typeof a).createdAt && a.reason === (b as typeof a).reason;

    case "working":
      return a.createdAt === (b as typeof a).createdAt && a.label === (b as typeof a).label;

    case "turn-group": {
      const bg = b as typeof a;
      return (
        a.startedAt === bg.startedAt &&
        a.endedAt === bg.endedAt &&
        a.live === bg.live &&
        a.sectionCount === bg.sectionCount &&
        isTurnStatusUnchanged(a.status, bg.status) &&
        a.items.length === bg.items.length &&
        a.items.every((item, index) => isTurnGroupItemUnchanged(item, bg.items[index]!))
      );
    }

    case "message": {
      const bm = b as typeof a;
      return (
        a.message === bm.message &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantCopyStreaming === bm.assistantCopyStreaming
      );
    }
  }
}
